<template>
  <div v-if="isDsrEdit" class="dsr-app">
    <router-view />
  </div>
  <div v-else-if="isMyDocumentReLogin">
    <router-view />
  </div>
  <AppLayout v-else>
    <template #topbar>
      <GlobalTopbar
        @select-entity="handleSelectEntity"
        @document-created="handleDocumentCreatedOld"
        @create-dsr="dsrTemplatePickerVisible = true"
        @create-document="showCreateDocumentModal = true"
      />
    </template>
    <template #sidebar>
      <GlobalSidebar
        v-if="isSmallScreenAndUp"
        @set-module-type="handleSetModuleType"
        @open-subscription-modal="openSubscriptionModal"
      />
    </template>
    <template #main-content>
      <TransitionFadeInOut mode="out-in">
        <div v-if="showRouterView" ref="mainContent">
          <router-view />
        </div>
        <div v-else class="router-view-loader-container">
          <FriedSpinner />
        </div>
      </TransitionFadeInOut>
    </template>
    <template v-if="showRouterView" #modals>
      <TransitionFadeInOut>
        <GlobalNavMenu
          v-show="isGlobalNavMenuOpen"
          v-if="isSmallScreenAndDown"
          @set-module-type="handleSetModuleType"
          @close="setGlobalNavMenuVisibility(false)"
          @document-created="handleDocumentCreatedOld"
          @create-dsr="dsrTemplatePickerVisible = true"
          @create-document="handleCreateDocumentSmallScreen"
          @open-subscription-modal="openSubscriptionModal"
        />
      </TransitionFadeInOut>
      <SubscriptionInfoModal
        v-if="showSubscriptionInfoModal && isActiveTrialPlan"
        @close="showSubscriptionInfoModal = false"
      />
      <SubscriptionWelcomeModal
        v-if="!subscriptionWelcomeModalShown && isActiveTrialPlan"
        @close="subscriptionWelcomeModalShown = true"
      />
      <AccessDeniedModal v-if="showAccessDeniedModal" @close="handleCloseAccessDeniedModal" />
      <IntersectionModal
        v-if="sessionStore.sessionIsLoaded && onboardingVisible"
        :user-hash="sessionStore.session.user_hash"
        @close="closeOnboarding"
        @document-created="handleDocumentCreatedOld"
      />
      <TemplateLoader
        v-if="showTemplateLoader"
        :template-id="templateId"
        @close="handleCloseTemplateLoader"
      />
      <EntityChangedModal v-if="entityChanged" @reload="handleReload" />
      <DsrTemplatePicker
        v-if="dsrTemplatePickerVisible"
        @close="dsrTemplatePickerVisible = false"
        @dsr-created="handleDocumentCreatedOld"
      />
      <CreateDocumentModal
        v-if="showCreateDocumentModal"
        :selected-type="selectedCreateDocumentSection"
        @close="handleCloseCreateDocument"
      />
      <LoadProductScreen
        v-if="loadProductScreenVisible"
        :active-module="modulesStore.activeModule"
      />
      <Toasts :offset="{ top: 72, left: 24, right: 24, bottom: 8 }" />
      <UpdatedAccessModal v-if="accessChanged" @close="handleUpdatedAccess" />
    </template>
  </AppLayout>
</template>

<script lang="ts" setup>
import BugsnagPerformance from '@bugsnag/browser-performance';
import { VueRouterRoutingProvider } from '@bugsnag/vue-router-performance';
import { friedTofuSettings } from '@getaccept/fried-tofu';
import Analytics from '@getaccept/lib-shared/src/analytics';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { ModuleName } from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import { LocalStorageKey } from '@getaccept/lib-shared/src/enums/local-storage';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { getGlobalHandle } from '@getaccept/lib-shared/src/helpers';
import { LocalStorageHelper } from '@getaccept/lib-shared/src/helpers/local-storage.helper';
import { RouterHelper } from '@getaccept/lib-shared/src/helpers/router.helper';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { pusherClientInternal } from '@getaccept/lib-shared/src/internal/pusher';
import { useAuthStore } from '@getaccept/lib-shared/src/internal/session/store/auth.store';
import { PortalConstants } from '@getaccept/lib-shared/src/portal/constants/portal.constants';
import { useChannelsStore } from '@getaccept/lib-shared/src/pusher/channels.store';
import type { SelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import EntityChangedModal from '@getaccept/lib-shared/src/session/components/EntityChangedModal.vue';
import { Toasts, useToast } from '@getaccept/lib-shared/src/toast';
import { AnalyticsEvent } from '@getaccept/lib-shared/src/types/analytics';
import type { Channel } from '@getaccept/pusher';
import { ChannelType, ConnectionState, UserEvent } from '@getaccept/pusher';
import { useLocalStorage, useStorage } from '@vueuse/core';
import { Settings } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';
import { setLocale as setYupLocale } from 'yup';
import { useAccessDeniedModalStore } from './access-denied/access-denied-modal.store';
import AccessDeniedModal from './access-denied/AccessDeniedModal.vue';
import AppLayout from './AppLayout.vue';
import { getTemplateIdFromRegistrationCampaign, hasValidString } from './common/helpers';
import { getTimezone } from './common/timezone/timezone.helper';
import { yupLocalizationKeys } from './common/yup-localization-keys';
import DsrTemplatePicker from './dsr-template-picker/DsrTemplatePicker.vue';
import { useTemplatePicker } from './dsr-template-picker/store/template-picker.store';
import { useEntityStore } from './entity/entity.store';
import { DeviceIdHelper } from './helpers/device-id.helper';
import { loadLanguageAsync } from './lang';
import { useModulesStore } from './modules/store/modules.store';
import IntersectionModal from './onboarding/components/IntersectionModal.vue';
import { useSessionStore } from './session/store/session.store';
import GlobalSidebar from './sidebar/components/GlobalSidebar.vue';
import LoadProductScreen from './sidebar/components/LoadProductScreen.vue';
import SubscriptionInfoModal from './subscription/components/SubscriptionInfoModal.vue';
import SubscriptionWelcomeModal from './subscription/components/SubscriptionWelcomeModal.vue';
import UpdatedAccessModal from './subscription/components/UpdatedAccessModal.vue';
import { useSubscriptionPusher } from './subscription/composables/subscription-pusher.composable';
import { useSubscriptionStore } from './subscription/store/subscription.store';
import TemplateLoader from './template-gallery/components/TemplateLoaderModal.vue';
import GlobalNavMenu from './topbar/components/GlobalNavMenu.vue';
import GlobalTopbar from './topbar/components/GlobalTopbar.vue';
import { useNavigationMenuStore } from './topbar/store/navigation-menu.store';
import { useUserStore } from './user/store/user.store';
import CreateDocumentModal from './document-create/components/CreateDocumentModal.vue';
import type { CreateDocumentSection } from './document-create/types/create-document-section';

const { toast } = useToast();
const showRouterView = ref(false);
const loadProductScreenVisible = ref(false);
const templatePickerStore = useTemplatePicker();
const { isVisible: dsrTemplatePickerVisible } = storeToRefs(templatePickerStore);
const showTemplateLoader = ref(false);
const templateId = ref(null);
const entityChanged = ref(false);
const onboardingVisible = ref(false);
const subscriptionStore = useSubscriptionStore();
const { setAccessChanged } = subscriptionStore;
const { isInactiveSubscription, isTrialPlan, accessChanged } = storeToRefs(subscriptionStore);

useSubscriptionPusher();

const { toggleAccessDeniedModal } = useAccessDeniedModalStore();
const { showAccessDeniedModal } = storeToRefs(useAccessDeniedModalStore());
const { isOpen: isGlobalNavMenuOpen } = storeToRefs(useNavigationMenuStore());
const { setOpen: setGlobalNavMenuVisibility } = useNavigationMenuStore();
const { isXSmallScreenAndDown, isSmallScreenAndDown, isSmallScreenAndUp } = useBreakpoints();

const entityStore = useEntityStore();
const sessionStore = useSessionStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const channelsStore = useChannelsStore();
const modulesStore = useModulesStore();

const router = useRouter();
const route = useRoute();

const isDsrEdit = computed(() => route.path.includes('/dsr-edit'));

const isMyDocumentReLogin = computed(() => route.path === '/my-documents-redirect');
const showCreateDocumentModal = ref(false);

const subscriptionWelcomeModalShown = useLocalStorage(
  `GA_SUBSCRIPTION_WELCOME_MODAL_SHOWN_${userStore.user.id}`,
  false
);

const showSubscriptionInfoModal = ref(subscriptionWelcomeModalShown.value);

const isActiveTrialPlan = computed(() => isTrialPlan.value && !isInactiveSubscription.value);

const openSubscriptionModal = () => {
  showSubscriptionInfoModal.value = true;
};

const setLocale = () => {
  Settings.defaultLocale = sessionStore.session.language;
  friedTofuSettings.locale = sessionStore.session.language;
  friedTofuSettings.zone = getTimezone();
  Settings.defaultZone = getTimezone();
  setYupLocale(yupLocalizationKeys);
};

const updateLanguage = () => {
  loadLanguageAsync(sessionStore.session.language);
  setLocale();
};

watch(() => sessionStore.session.language, updateLanguage);

const closeOnboarding = () => {
  onboardingVisible.value = false;
};

const handleCloseAccessDeniedModal = () => {
  toggleAccessDeniedModal(false);
};

const handleCloseTemplateLoader = () => {
  LocalStorageHelper.reset(LocalStorageKey.Template);
  showTemplateLoader.value = false;
};

const initiateAnalytics = () => {
  if (getGlobalHandle().Intercom) {
    getGlobalHandle().Intercom('update', {
      app_id: import.meta.env.VUE_APP_INTERCOM_APP_ID || '',
      hide_default_launcher: true,
      language_override: sessionStore.session.language,
      user_hash: sessionStore.session.iio_hash,
    });
  }

  const analyticsScriptLoaded =
    // eslint-disable-next-line no-prototype-builtins
    !!getGlobalHandle().analytics.hasOwnProperty('initialized');

  if (analyticsScriptLoaded) {
    Analytics.initializeCoreApp(
      sessionStore.session,
      userStore.user,
      entityStore.entity,
      modulesStore.allowedModules
    );
  } else {
    setTimeout(() => {
      Analytics.initializeCoreApp(
        sessionStore.session,
        userStore.user,
        entityStore.entity,
        modulesStore.allowedModules
      );
    }, 10000);
  }
};

const setBugsnagMetadata = () => {
  if (!bugsnagClient || !sessionStore.session) {
    return;
  }
  bugsnagClient.addMetadata('entity', {
    id: sessionStore.session.entity_hash,
    name: sessionStore.session.entity_name,
  });
  bugsnagClient.addMetadata('user', {
    user: {
      id: sessionStore.session.user_hash,
    },
  });
};

const handleSelectEntity = (entity: SelectEntity) => {
  if (entity.auth) {
    entityStore.authEntity(entity);
    return;
  }
  entityStore.selectEntity(entity.id);
};

const unhandledrejectionHandler = event => {
  if (bugsnagClient) {
    bugsnagClient.notify(event);
  }
};

const deviceId = useStorage<string>('deviceId', DeviceIdHelper.generate());
const generateDeviceId = () => {
  if (!deviceId.value) {
    deviceId.value = DeviceIdHelper.generate();
  }
};

const handleStorageChange = () => {
  const entityId: string = localStorage.getItem('GA_ACTIVE_ENTITY');
  if (entityId && sessionStore.session.entity_hash !== entityId) {
    entityChanged.value = true;
  }
  generateDeviceId();
};

const addListeners = async () => {
  channelsStore.setUserChannel(await pusherClientInternal.subscribe({ type: ChannelType.User }));
  entityStore.pusherChannel = await pusherClientInternal.subscribe({
    type: ChannelType.Entity,
  });

  pusherClientInternal.connectionListen(ConnectionState.Error, e => {
    const pusherErrors = [1006, 4201, 4200];
    if (
      pusherErrors.includes(e.data?.code) ||
      pusherErrors.includes(e.error?.data?.code) ||
      (e.error && Object.prototype.hasOwnProperty.call(e.error, 'isTrusted')) ||
      Object.prototype.hasOwnProperty.call(e, 'isTrusted')
    ) {
      return;
    }
    bugsnagClient.notify(e);
  });

  pusherClientInternal.listen(
    channelsStore.userChannel as Channel,
    UserEvent.UploadContactsDone,
    () => {
      toast.success(t('upload-contacts-success-alert-description'));
    }
  );

  window.addEventListener('unhandledrejection', unhandledrejectionHandler);
  window.addEventListener('storage', handleStorageChange);
};

const handleReload = () => {
  location.replace('/');
};

const handleShowIntro = () => {
  if (!route.query) {
    router.replace('/dashboard?intro');
  }
};

const handleTemplateIdRegistrationCampaign = () => {
  let templateIdFromRegistrationCampaign = null;

  if (sessionStore.session?.registration_campaign?.includes('templateid_')) {
    templateIdFromRegistrationCampaign = getTemplateIdFromRegistrationCampaign(
      sessionStore.session.registration_campaign
    );
  }
  if (hasValidString(templateId.value)) {
    router.replace(RouterHelper.dashboardPath);
    templateId.value = templateIdFromRegistrationCampaign;
    showTemplateLoader.value = true;
  } else {
    handleShowIntro();
  }
};

const showLoadProductScreen = async () => {
  loadProductScreenVisible.value = true;
  await new Promise(resolve => setTimeout(resolve, 1200));
  loadProductScreenVisible.value = false;
};

const closeMobileGlobalNav = () => {
  if (isXSmallScreenAndDown.value) {
    setTimeout(() => setGlobalNavMenuVisibility(false), 1000);
  }
};

const handleSetModuleType = async (module: ModuleName) => {
  closeMobileGlobalNav();
  await showLoadProductScreen();

  modulesStore.setActiveModule(module);

  switch (module) {
    case ModuleName.DealRooms:
      router.push({ name: PathName.Rooms });
      return;
    case ModuleName.Contracts:
      router.push({ name: PathName.Dashboard });
      return;
    default:
      router.push({ name: PathName.Root });
  }
};

const selectedCreateDocumentSection = ref<CreateDocumentSection>(undefined);

const handleCloseCreateDocument = () => {
  showCreateDocumentModal.value = false;
  selectedCreateDocumentSection.value = undefined;
};

const handleCreateDocumentSmallScreen = (type?: CreateDocumentSection) => {
  setGlobalNavMenuVisibility(false);
  selectedCreateDocumentSection.value = type;
  showCreateDocumentModal.value = true;
};

const handleDocumentCreatedOld = ({ id, type }: { id: string; type: EnumDocumentType }) => {
  if (type === EnumDocumentType.DSR) {
    dsrTemplatePickerVisible.value = false;
    router.push({
      name: PathName.DsrEdit,
      params: { id },
    });
    closeMobileGlobalNav();
    return;
  }
  Analytics.track(AnalyticsEvent.CreatedDocument, {
    documentType: type,
  });
  router.push(`/document/edit/${id}`);
  closeMobileGlobalNav();
};

const handleUpdatedAccess = () => {
  setAccessChanged(false);
  window.location.replace(window.location.href);
};

const initiateRouterGuards = () => {
  router.afterEach((to: RouteLocationNormalized) => {
    if ('intro' in to.query) {
      onboardingVisible.value = true;
    }
  });

  if ('intro' in route.query) {
    onboardingVisible.value = true;
  }
};

const preventIosInputZoom = () => {
  if (navigator.userAgent.indexOf('iPhone') > -1) {
    document
      .querySelector('[name=viewport]')
      .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
  }
};

const initBugSnagPerformanceMonitoring = () => {
  if (import.meta.env.VUE_APP_BUGSNAG) {
    BugsnagPerformance.start({
      apiKey: import.meta.env.VUE_APP_BUGSNAG,
      releaseStage: import.meta.env.VUE_APP_BUGSNAG_RELEASE_STAGE || 'development',
      appVersion: import.meta.env.VUE_APP_BUGSNAG_APP_VERSION || '0.0.0',
      routingProvider: new VueRouterRoutingProvider(router, '/'),
    });
  }
};

const init = async () => {
  friedTofuSettings.modalTeleportTo = `#${PortalConstants.elementId}`;
  preventIosInputZoom();
  try {
    await pusherClientInternal.connect(authStore.token);
  } catch (e) {
    bugsnagClient?.notify(e);
  }

  initiateRouterGuards();
  setLocale();
  setBugsnagMetadata();
  initiateAnalytics();
  generateDeviceId();

  try {
    await addListeners();
  } catch (e) {
    bugsnagClient?.notify(e);
  }

  initBugSnagPerformanceMonitoring();

  showRouterView.value = true;

  templateId.value = LocalStorageHelper.getLocalStorage(LocalStorageKey.Template);
  if (templateId.value) {
    showTemplateLoader.value = true;
  } else if ('intro' in route.query) {
    handleTemplateIdRegistrationCampaign();
  }
};

init();

onBeforeUnmount(() => {
  window.removeEventListener('unhandledrejection', unhandledrejectionHandler);
  window.removeEventListener('storage', handleStorageChange);
});
</script>

<style lang="scss">
@import '@getaccept/lib-shared/src/chat/assets/variables';

#app {
  height: 100%;
}

.dsr-app {
  width: 100%;
  height: 100%;
}

.engage-video-modal {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.router-view-loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.recipient-authorization-container {
  i[class*='icon-'] {
    margin-right: 4px;
  }
}

.transport-out-engage-leave-active {
  animation: slide-out-engage-video 0.4s;
  animation-fill-mode: forwards;
  background-color: transparent;
  position: absolute;
}

@keyframes slide-out-engage-video {
  0% {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  100% {
    top: 20rem;
    left: 100%;
    height: 12em;
    width: 20em;
    opacity: 0;
  }
}
</style>
